import axios, { type AxiosError } from 'axios';

/**
 * Every failure that leaves the transport layer is an ApiError. `kind` is derived from the
 * HTTP status on the client so hooks and pages can switch on a small set; `code` is the
 * backend's stable machine code for user-facing messages (i18n key `errors:code.<CODE>`),
 * never `message`.
 */
export type ApiErrorKind =
  | 'validation'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'server'
  | 'network'
  | 'timeout'
  | 'cancelled'
  | 'unknown';

export interface ApiFieldError {
  /** Dot path prefixed by its source: "body.variants.0.sku", "params.id", "query.cursor". */
  field: string;
  code: string;
  message: string;
}

/** Envelope the backend returns with every non-2xx status. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    requestId: string;
    details?: ApiFieldError[];
    meta?: Record<string, unknown>;
  };
}

/** Synthetic codes for failures that never reached the API or came back malformed. */
export const TRANSPORT_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
  UNEXPECTED_RESPONSE: 'UNEXPECTED_RESPONSE',
} as const;

const RETRYABLE_KINDS: ReadonlySet<ApiErrorKind> = new Set([
  'network',
  'timeout',
  'rate_limited',
  'server',
]);

export interface ApiErrorInit {
  kind: ApiErrorKind;
  code: string;
  message: string;
  status?: number | undefined;
  requestId?: string | undefined;
  details?: ApiFieldError[] | undefined;
  meta?: Record<string, unknown> | undefined;
  cause?: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly kind: ApiErrorKind;
  readonly code: string;
  readonly status: number | undefined;
  readonly requestId: string | undefined;
  /** Field errors grouped by their full source-prefixed path. Empty object when there are none. */
  readonly fieldErrors: Readonly<Record<string, ApiFieldError[]>>;
  readonly details: readonly ApiFieldError[];
  /** Free-form context some codes carry (limits, retry hints). */
  readonly meta: Readonly<Record<string, unknown>>;
  readonly retryable: boolean;

  constructor(init: ApiErrorInit) {
    super(init.message, { cause: init.cause });
    this.kind = init.kind;
    this.code = init.code;
    this.status = init.status;
    this.requestId = init.requestId;
    this.details = init.details ?? [];
    this.fieldErrors = groupByField(this.details);
    this.meta = init.meta ?? {};
    this.retryable = RETRYABLE_KINDS.has(init.kind);
  }

  get hasFieldErrors(): boolean {
    return this.details.length > 0;
  }

  /** Builds an ApiError from anything a request can throw. Idempotent for ApiError input. */
  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error;
    if (axios.isCancel(error)) {
      return new ApiError({
        kind: 'cancelled',
        code: TRANSPORT_CODES.CANCELLED,
        message: 'Request canceled',
        cause: error,
      });
    }
    if (axios.isAxiosError(error)) return ApiError.fromAxios(error);
    const message = error instanceof Error ? error.message : 'Unknown error';
    return new ApiError({
      kind: 'unknown',
      code: TRANSPORT_CODES.UNEXPECTED_RESPONSE,
      message,
      cause: error,
    });
  }

  static fromAxios(error: AxiosError): ApiError {
    const { response } = error;
    if (!response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      return new ApiError({
        kind: timedOut ? 'timeout' : 'network',
        code: timedOut ? TRANSPORT_CODES.TIMEOUT : TRANSPORT_CODES.NETWORK_ERROR,
        message: timedOut ? 'The request timed out' : 'Could not reach the server',
        cause: error,
      });
    }
    const requestIdHeader = readHeader(response.headers, 'x-request-id');
    const body: unknown = response.data;
    if (!isApiErrorBody(body)) {
      return new ApiError({
        kind: response.status >= 500 ? 'server' : 'unknown',
        code: TRANSPORT_CODES.UNEXPECTED_RESPONSE,
        message: `Unexpected ${response.status} response`,
        status: response.status,
        requestId: requestIdHeader,
        cause: error,
      });
    }
    const { code, message, details, meta, requestId } = body.error;
    return new ApiError({
      kind: kindFromStatus(response.status),
      code,
      message,
      status: response.status,
      requestId: requestId || requestIdHeader,
      details,
      meta,
      cause: error,
    });
  }
}

export function kindFromStatus(status: number): ApiErrorKind {
  if (status === 400 || status === 422) return 'validation';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === 'string' && code.length > 0 && typeof message === 'string';
}

/** "body.variants.0.sku" → "variants.0.sku". Params and query paths keep their prefix. */
export function formFieldPath(field: string): string {
  return field.startsWith('body.') ? field.slice('body.'.length) : field;
}

function groupByField(details: readonly ApiFieldError[]): Record<string, ApiFieldError[]> {
  const grouped: Record<string, ApiFieldError[]> = {};
  for (const detail of details) {
    (grouped[detail.field] ??= []).push(detail);
  }
  return grouped;
}

function readHeader(headers: unknown, name: string): string | undefined {
  if (typeof headers !== 'object' || headers === null) return undefined;
  const value = (headers as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : undefined;
}
